import { useEffect, useCallback } from 'react';
import { RefreshCw, ClipboardList, TrendingUp, Clock, AlertTriangle } from 'lucide-react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { Skeleton, PanelSkeleton, TableSkeleton } from '@/components/ui/skeleton';
import { useDashboardStore } from '../store';
import {
  KpiCard,
  RequestAgingChart,
  MovementTrendChart,
  RecentMrsTable,
  TopRequestorsTable,
  FastMovingItemsTable,
} from '../components';

export function MroDashboardPage() {
  usePageTitle('MRO Dashboard');
  const {
    summary,
    recentMrs,
    topRequestors,
    fastMovingItems,
    requestAging,
    movementTrends,
    isLoading,
    error,
    lastRefresh,
    autoRefreshEnabled,
    refreshInterval,
    fetchAll,
    clearError,
  } = useDashboardStore();

  // Initial fetch
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefreshEnabled) return;

    const interval = setInterval(() => {
      fetchAll();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefreshEnabled, refreshInterval, fetchAll]);

  const handleRefresh = useCallback(() => {
    fetchAll();
  }, [fetchAll]);

  if (isLoading && !summary) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="MRO Dashboard"
          description="Maintenance, Repair & Operations Inventory Overview"
        />
        {/* KPI Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2 rounded-lg border p-4">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-7 w-16" />
              <Skeleton className="h-3 w-20" />
            </div>
          ))}
        </div>
        {/* Charts Row */}
        <div className="grid gap-4 lg:grid-cols-2">
          <PanelSkeleton rows={3} />
          <PanelSkeleton rows={3} />
        </div>
        {/* Tables Row */}
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-lg border p-4">
            <TableSkeleton rows={5} cols={3} />
          </div>
          <div className="rounded-lg border p-4">
            <TableSkeleton rows={5} cols={3} />
          </div>
        </div>
        {/* Fast Moving Items */}
        <div className="rounded-lg border p-4">
          <TableSkeleton rows={5} cols={4} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
        <p className="text-destructive">{error}</p>
        <Button onClick={() => { clearError(); fetchAll(); }}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="MRO Dashboard"
        description="Maintenance, Repair & Operations Inventory Overview"
        actions={
          <>
            {lastRefresh && (
              <span className="text-xs text-muted-foreground">
                Last updated: {new Date(lastRefresh).toLocaleTimeString()}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </>
        }
      />

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Pending MRS"
          value={summary?.pendingMrsCount ?? 0}
          subtitle="Awaiting issuance"
          icon={ClipboardList}
        />
        <KpiCard
          title="Fulfillment Rate"
          value={`${summary?.fulfillmentRatePercent?.toFixed(1) ?? 0}%`}
          subtitle="Last 30 days"
          icon={TrendingUp}
        />
        <KpiCard
          title="Avg. Fulfillment Time"
          value={`${summary?.avgFulfillmentDays?.toFixed(1) ?? 0} days`}
          subtitle="Request to issuance"
          icon={Clock}
        />
        <KpiCard
          title="Low Stock Alerts"
          value={summary?.lowStockAlertCount ?? 0}
          subtitle="Items below threshold"
          icon={AlertTriangle}
        />
      </div>

      {/* Charts Row */}
      <div className="grid gap-4 lg:grid-cols-2">
        <RequestAgingChart data={requestAging} />
        <MovementTrendChart data={movementTrends} />
      </div>

      {/* Tables Row */}
      <div className="grid gap-4 lg:grid-cols-2">
        <RecentMrsTable data={recentMrs} />
        <TopRequestorsTable data={topRequestors} />
      </div>

      {/* Fast Moving Items */}
      <div className="grid gap-4 lg:grid-cols-1">
        <FastMovingItemsTable data={fastMovingItems} />
      </div>
    </div>
  );
}
